import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { usePermission } from '@/hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { Permission } from '@/types/domain';
import { Zap, Plus, CheckSquare, Layers, Users, ShoppingBag, BarChart2, LucideIcon } from 'lucide-react';

export const QuickActionsPanel: React.FC = () => {
  const { can } = usePermission();
  const navigate = useNavigate();

  const actions: { id: string; label: string; icon: LucideIcon; permission: Permission; onClick: () => void; highlight?: boolean }[] = [
    {
      id: 'act-req',
      label: 'Create Requisition',
      icon: Plus,
      permission: 'REQUEST_CREATE',
      onClick: () => navigate(ROUTES.REQUESTS.NEW),
      highlight: true,
    },
    {
      id: 'act-appr',
      label: 'Review Approvals',
      icon: CheckSquare,
      permission: 'REQUEST_APPROVE',
      onClick: () => navigate(ROUTES.APPROVALS.LIST),
    },
    {
      id: 'act-rfq',
      label: 'Manage RFQs',
      icon: Layers,
      permission: 'RFQ_VIEW',
      onClick: () => navigate(ROUTES.RFQ.LIST),
    },
    {
      id: 'act-vendor',
      label: 'Vendor Directory',
      icon: Users,
      permission: 'VENDOR_VIEW',
      onClick: () => navigate(ROUTES.VENDORS.LIST),
    },
    {
      id: 'act-po',
      label: 'Purchase Orders',
      icon: ShoppingBag,
      permission: 'PO_VIEW',
      onClick: () => navigate(ROUTES.PURCHASE_ORDERS.LIST),
    },
    {
      id: 'act-pr',
      label: 'SAP PR Console',
      icon: BarChart2,
      permission: 'PR_VIEW',
      onClick: () => navigate(ROUTES.PR.LIST),
    },
  ];

  const visibleActions = actions.filter((a) => can(a.permission));

  return (
    <Card className="border-border rounded-2xl shadow-2xs">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          Quick Actions Hub
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-4">
        <div className="grid grid-cols-2 gap-2.5">
          {visibleActions.map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.id}
                onClick={action.onClick}
                className={`flex flex-col items-center justify-center p-3.5 rounded-xl border transition-all text-center group ${
                  action.highlight
                    ? 'bg-primary text-primary-foreground border-primary shadow-2xs hover:bg-primary-dark'
                    : 'bg-card border-border hover:border-primary/50 hover:bg-muted/50 text-foreground'
                }`}
              >
                <Icon className={`h-5 w-5 mb-2 group-hover:scale-110 transition-transform ${action.highlight ? 'text-white' : 'text-primary'}`} />
                <span className="font-heading text-xs font-bold leading-tight">{action.label}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
